<template>
  <base-wrapper>
    <div class="grid grid-cols-1 gap-4">
      <base-card title="Tambah / Update Mitra Usaha">
        <div class="grid grid-cols-1 gap-4">
          <base-input
            label="File Mitra Usaha Untuk Ditambahkan"
            type="file"
            :shadow="false"
            size="padding-md"
            fullwidth
            accept=".csv"
            @change="onChangeFile"
          />
          <div class="col-span-full flex items-center gap-2">
            <base-button
              v-if="validationSuccess"
              :loading="processLoading"
              :disabled="validateLoading"
              @click="onProcess"
              >{{ processStatusMessage }}</base-button
            >
            <base-button
              v-else
              :loading="validateLoading"
              :disabled="processLoading"
              @click="onValidate"
              >{{ validateStatusMessage }}</base-button
            >
          </div>
          <alert-message
            v-if="validationSuccess"
            type="success"
            message="Validasi Berhasil"
          />
          <div v-if="validationError.length" class="col-span-full">
            <div class="space-y-1 rounded-lg bg-red-50 p-4">
              <a :href="fileValidateErorrUrl" class="font-bold text-red-800 flex items-center gap-2 underline">
                Download File Error
              </a>
              <p
                v-for="(error, i) in validationError"
                :key="i"
                class="text-sm font-semibold text-red-800"
              >
                {{ error }}
              </p>
            </div>
          </div>
        </div>
      </base-card>
      <base-card title="Keterangan">
        <dl class="grid gap-4">
          <div class="w-full">
            <dt class="text-sm text-gray-900">
              Contoh file mitra usaha untuk ditambah / diupdate
            </dt>
            <dd class="mt-1">
              <a
                href="/tool-bonus-import-example/tambah-update-mitra-usaha.csv"
                class="text-sm font-medium text-green-600"
                >tambah-update-mitra-usaha.csv</a
              >
            </dd>
          </div>
        </dl>
      </base-card>
      <base-card title="Riwayat Tambah / Update Mitra Usaha">
        <datatable
          :total="histories.meta.page.total"
          :perPage="histories.meta.page.perPage"
          :currentPage="histories.meta.page.currentPage"
          @pagechanged="onChangePageHistories"
          :columns="[
            { id: 'date', name: 'Tanggal' },
            { id: 'finish_date', name: 'Selesai' },
            { id: 'user', name: 'User' },
            { id: 'total', name: 'Total' },
            { id: 'status', name: 'Status' },
            { id: 'download', name: 'Download' },
          ]"
        >
          <template #tbody="{ classes }">
            <tr
              v-for="history in histories.data"
              :key="history.id"
              :class="classes.tr"
            >
              <td :class="classes.td">
                {{ history.attributes.createdAt | formatDate }}
              </td>
              <td :class="classes.td">
                {{ history.attributes.updatedAt | formatDate }}
              </td>
              <td :class="classes.td">
                {{ history.attributes.user_email || '-' }}
              </td>
              <td :class="classes.td">
                {{ history.attributes.total_user }} / 
                {{ history.attributes.total_user_synced }}
		({{ history.attributes.total_rows }})
              </td>
              <td :class="classes.td">
                <base-badge
                  :color="
                    history.attributes.status !== 'finished'
                      ? 'yellow'
                      : (history.attributes.has_error ? 'red' : 'green')
                  "
                  >{{
                    processStatusMessageList[history.attributes.status]
                  }}</base-badge
                >
              </td>
              <td :class="classes.td">
                <div class="flex gap-2">
                  <a
                    :href="history.attributes.fileurl"
                    class="font-semibold text-green-600"
                    >Download File Import</a
                  >
                  <a
                    v-if="history.attributes.has_error"
                    :href="history.attributes.error_fileurl"
                    class="font-semibold text-red-600"
                    >Download File Error</a
                  >
                </div>
              </td>
            </tr>
          </template>
        </datatable>
      </base-card>
      <base-card
        title="Riwayat Error Tambah / Update Mitra Usaha"
        class="space-y-4"
      >
        <div class="flex justify-end gap-4">
          <base-input
            label="Tanggal"
            type="date"
            :shadow="false"
            v-model="filterImportErrors.date"
            @change="loadImportErrors"
          />
          <base-input
            label="Tag"
          >
            <base-select :shadow="false" :options="filterImportErrorTagOptions" v-model="filterImportErrors.tag" @change="loadImportErrors" />
          </base-input>
          <base-input
            label="Tipe"
          >
            <base-select :shadow="false" :options="filterImportErrorTypeOptions" v-model="filterImportErrors.type" @change="loadImportErrors" />
          </base-input>
          <base-input
            label="Cari Kantor"
            placeholder="Cari Kode Kantor"
            type="text"
            :shadow="false"
            debounce
            v-model="filterImportErrors.search"
            @native-input="loadImportErrors"
          />
        </div>
        <datatable
          :total="importErrors.meta.page.total"
          :perPage="importErrors.meta.page.perPage"
          :currentPage="importErrors.meta.page.currentPage"
          @pagechanged="onChangePageImportErrors"
          :columns="[
            { id: 'date', name: 'Tanggal' },
            { id: 'tag', name: 'Tag' },
            { id: 'type', name: 'Tipe' },
            { id: 'filepath', name: 'File' },
            { id: 'office', name: 'Kode Mitra Usaha' },
            { id: 'message', name: 'Deskripsi' },
          ]"
        >
          <template #tbody="{ classes }">
            <tr
              v-for="importError in importErrors.data"
              :key="importError.id"
              :class="classes.tr"
            >
              <td :class="classes.td">
                {{ importError.attributes.createdAt | formatDate }}
              </td>
              <td :class="classes.td">
                {{ errorTagList[importError.attributes.tag] }}
              </td>
              <td :class="classes.td">
                {{ errorTypeList[importError.attributes.error_type] }}
              </td>
              <td :class="classes.td">
                {{ getFileNameFromPath(importError.attributes.filepath) }}
              </td>
              <td :class="classes.td">
                {{ importError.attributes.office_code }}
              </td>
              <td :class="classes.td">
                {{ importError.attributes.message }}
              </td>
            </tr>
          </template>
        </datatable>
      </base-card>
    </div>
    <base-confirm
      message="Apakah anda yakin akan menambahkan mitra usaha yang diupload?"
      :visible="confirming"
      @close="confirming = false"
      @confirmed="onConfirmed"
    ></base-confirm>
    <loading
      v-if="historiesLoading || jobStatusLoading || importErrorsLoading"
    />
  </base-wrapper>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { requestMixin } from '@/mixins/request/request';
import BaseConfirm from '@/components/base/BaseConfirm.vue';
import { validateFileCsv } from '@/utils/misc';
import AlertMessage from '@/components/base/AlertMessage.vue';

export default {
  mixins: [requestMixin],
  components: { BaseConfirm, AlertMessage },
  data() {
    return {
      processLoading: false,
      validateLoading: false,
      fileValidateErorrUrl: null,
      historiesLoading: false,
      importErrorsLoading: false,
      jobStatusLoading: false,
      histories: {
        data: [],
        meta: {
          page: {},
        },
      },
      importErrors: {
        data: [],
        meta: {
          page: {},
        },
      },
      validationError: [],
      validationSuccess: false,
      confirming: false,
      processStatus: 'null',
      validateStatus: 'null',
      form: {
        file: null,
      },
      channelName: null,
      filterImportErrors: {
        date: null,
        search: null,
        tag: null,
        type: null
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    errorTagList() {
      return {
        import_office: 'Import Kantor',
        import_warehouse: 'Import Gudang',
        import_user: 'Import User',
      };
    },
    errorTypeList() {
      return {
        import_user_failed_sync: 'Gagal tambah user ke db mitra',
        import_office_ktp_already_used: 'KTP sudah digunakan',
        import_office_bank_already_used: 'Nomor Bank sudah digunakan',
        import_office_invalid_email: 'Email tidak valid',
        import_office_invalid_period: 'Periode tidak valid',
        import_user_email_already_used: 'Email sudah digunakan',
        import_user_cellphone_already_used: 'No Telp sudah digunakan',
        import_user_invalid_email: 'Email tidak valid',
        import_user_invalid_pin: 'PIN tidak valid'
      }
    },
    filterImportErrorTagOptions() {
      return [
        {
          key: 'null',
          value: null,
          label: 'Semua Tag'
        },
        ...Object.entries(this.errorTagList)
          .map(([key, value]) => ({
            key: key,
            value: key,
            label: value
          }))
      ]
    },
    filterImportErrorTypeOptions() {
      return [
        {
          key: 'null',
          value: null,
          label: 'Semua Tipe'
        },
        ...Object.entries(this.errorTypeList)
          .map(([key, value]) => ({
            key: key,
            value: key,
            label: value
          }))
      ]
    },
    processStatusMessageList() {
      return {
        null: 'Proses',
        request: 'Mengirim ke Server',
        wait: 'Menunggu Diproses',
        queued: 'Menunggu Antrian',
        started: 'Mulai Diproses',
        'warehouse-importing': 'Menambahkan Gudang',
        'member-importing': 'Menambahkan Kantor',
        'member-warehouse-importing': 'Menambahkan Kantor Gudang',
        'user-importing': 'Menambahkan Akun',
        'upline-importing': 'Menambahkan Upline',
        'role-importing': 'Menambahkan Role',
        'pin-hashing': 'Menenkripsi PIN',
        'send-email': 'Mengirim Email',
        'send-whatsapp': 'Mengirin Whatsapp',
        finished: 'Selesai',
      };
    },
    processStatusMessage() {
      return this.processStatusMessageList[this.processStatus];
    },
    validateStatusMessage() {
      return {
        null: 'Validasi',
        request: 'Mengirim ke Server',
        wait: 'Menunggu Diproses',
        started: 'Mulai Diproses',
      }[this.validateStatus];
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    getFileNameFromPath(path) {
      if (!path) {
        return path;
      }

      return path.split('bulk_create_mitra_usaha/')[1];
    },
    listenToEvent() {
      this.channelName = `private-OfficeBulkCreateMitraUsaha.${this.me.id}`;

      const channel = this.$pusher.subscribe(this.channelName);

      channel.bind('OfficeBulkCreateMitraUsahaStatusUpdated', (e) => {
        if (e.status === 'error') {
          this.setProcessStatus('null');
          this.stopProcessLoading();
          this.createAlert({
            data: e.message,
            status: 'error',
          });
        } else if (e.status === 'finished') {
          this.setProcessStatus('null');
          this.stopProcessLoading();
          this.createAlert({
            data: 'Berhasil menambahkan mitra usaha',
            status: 'success',
          });
          this.loadHistories();
          this.loadImportErrors();
        } else {
          this.processStatus = e.status;
        }
      });

      channel.bind('BatchValidateImportMemberStatusUpdated', (e) => {
        if (e.status === 'processing') {
          this.setValidateStatus('started');
        } else if (e.status === 'has-error') {
          const errors = JSON.parse(e.message);

          errors.forEach((error) => {
            this.validationError.push(error);
          });
        } else if (e.status === 'finished-with-error') {
          this.setValidateStatus('null');
          this.stopValidateLoading();
          this.fileValidateErorrUrl = e.file_url
        } else {
          this.setValidateStatus('null');
          this.stopValidateLoading();
          this.validationSuccess = true;
        }
      });
    },
    async loadJobStatus() {
      this.jobStatusLoading = true;

      const [res, err] = await this.request(
        '/api/v1/users/-actions/check-job-status',
        {
          params: {
            type: 'batch_import_member',
          },
        }
      );

      if (!err) {
        const { running, status } = res;

        if (running) {
          if (status !== 'error' && status !== 'finished') {
            this.setProcessStatus(status);
            this.startProcessLoading();
          }
        }
      }

      this.jobStatusLoading = false;
    },
    async loadHistories(params) {
      this.historiesLoading = true;

      const [res, err] = await this.request(
        `/api/v1/batch-import-member-histories`,
        {
          params: {
            'fields[batch-import-member-histories]':
              'createdAt,updatedAt,status,fileurl,error_fileurl,has_error,user_email,total_user,total_user_synced,total_rows',
            'page[size]': 5,
            sort: '-id',
            ...params,
          },
        }
      );

      if (!err) {
        this.histories = res;
      }

      this.historiesLoading = false;
    },
    async loadImportErrors(params) {
      this.importErrorsLoading = true;

      const [res, err] = await this.request(`/api/v1/import-member-errors`, {
        params: {
          'fields[import-member-errors]':
            'createdAt,tag,error_type,office_code,message,filepath',
          'page[size]': 5,
          'filter[date]': this.filterImportErrors.date,
          'filter[search]': this.filterImportErrors.search,
          'filter[tag]': this.filterImportErrors.tag,
          'filter[type]': this.filterImportErrors.type,
          'filter[reserved]': true,
          sort: '-id',
          ...params,
        },
      });

      if (!err) {
        this.importErrors = res;
      }

      this.importErrorsLoading = false;
    },
    onChangeFile(e) {
      this.form.file = e.target.files[0];
    },
    async onProcess() {
      if (!validateFileCsv(this.form.file)) {
        this.createAlert({
          data: 'File yang diupload harus bertipe csv',
          status: 'error',
        });
      } else {
        this.confirming = true;
      }
    },
    async onConfirmed() {
      this.confirming = false;

      this.setProcessStatus('request');
      this.startProcessLoading();

      const formData = new FormData();

      formData.append('file', this.form.file);

      const [, error] = await this.request(
        '/api/v1/offices/-actions/bulk-create-mitra-usaha',
        {
          method: 'post',
          data: formData,
        }
      );

      if (error) {
        this.createAlert({
          data: this.getRequestErrorMessage(error),
          status: 'error',
        });
        this.setProcessStatus('null');
        this.stopProcessLoading();
      } else {
        this.setProcessStatus('wait');
      }
    },
    async onValidate() {
      if (!validateFileCsv(this.form.file)) {
        this.createAlert({
          data: 'File yang diupload harus bertipe csv',
          status: 'error',
        });
      } else {
        this.validationError = [];
        this.validationSuccess = false;

        this.setValidateStatus('request');
        this.startValidateLoading();

        const formData = new FormData();

        formData.append('file', this.form.file);
        formData.append('validate', true);

        const [, error] = await this.request(
          '/api/v1/offices/-actions/bulk-create-mitra-usaha',
          {
            method: 'post',
            data: formData,
          }
        );

        if (error) {
          this.createAlert({
            data: this.getRequestErrorMessage(error),
            status: 'error',
          });
          this.setValidateStatus('null');
          this.stopValidateLoading();
        } else {
          this.setValidateStatus('wait');
        }
      }
    },
    onChangePageHistories(page) {
      this.loadHistories({
        'page[number]': page,
      });
    },
    onChangePageImportErrors(page) {
      this.loadImportErrors({
        'page[number]': page,
      });
    },
    setProcessStatus(status) {
      this.processStatus = status;
    },
    startProcessLoading() {
      this.processLoading = true;
    },
    stopProcessLoading() {
      this.processLoading = false;
    },
    setValidateStatus(status) {
      this.validateStatus = status;
    },
    startValidateLoading() {
      this.validateLoading = true;
    },
    stopValidateLoading() {
      this.validateLoading = false;
    },
    stopListenToEvent() {
      if (this.me) {
        this.$pusher.unsubscribe(this.channelName);
      }
    },
  },
  created() {
    this.listenToEvent();
    this.loadHistories();
    this.loadJobStatus();
    this.loadImportErrors();
  },
  destroyed() {
    this.stopListenToEvent();
  },
};
</script>
